import React, { useState, useRef, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import useValidatedForm from './forms/useValidatedForm';

const GRADIENT = 'linear-gradient(135deg, #6B4EE8 0%, #4A90E2 100%)';

/**
 * SupportWidget — the header's support icon + chat-style panel.
 *
 * One-way "message us": the merchant types a message, Send emails it to
 * our support inbox as "User Support #XXXX" (with their shop + contact
 * email attached server-side), and the panel replies with a friendly
 * auto-response. Not a live chat.
 */
export default function SupportWidget({ shop }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [thread, setThread] = useState([
    { from: 'us', text: 'Hi! 👋 Need a hand or found something odd? Send us a message and our team will get back to you by email.' },
  ]);
  const rootRef = useRef(null);
  const scrollRef = useRef(null);

  // Close on outside click / Escape — same behavior as the bell panel.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Keep the newest message visible by scrolling ONLY the message list.
  // (scrollIntoView would scroll every ancestor — including the page —
  // which visibly shifted the whole top bar down when the panel opened.)
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread, open]);

  // Validated-form pattern: an empty Send is never a silent no-op — the
  // textarea turns red with "Please type a message first." and keeps focus.
  const composer = useValidatedForm({
    fields: [{ id: 'supportMessage', label: 'Message', required: true, errorMessage: 'Please type a message first.' }],
    values: { supportMessage: draft },
  });

  const send = async () => {
    const message = draft.trim();
    if (sending || !shop) return;
    if (!message) { composer.submit(); return; }
    setSending(true);
    setThread(t => [...t, { from: 'me', text: message }]);
    setDraft('');
    try {
      const res = await apiFetch(`/api/support/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ shop, message }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Send failed');
      setThread(t => [...t, {
        from: 'us',
        text: `Thank you for reaching out! You'll receive an email from one of our team members soon. (Ticket #${data.ticket})`,
      }]);
    } catch {
      setThread(t => [...t, {
        from: 'us',
        text: "Hmm, that didn't send — please try again in a moment.",
        error: true,
      }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      {/* Gradient chat-bubble icon — sits left of the notifications bell. */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Contact support"
        title="Contact support"
        style={{
          position: 'relative',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
            stroke="url(#supportGradient)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="8.5" cy="11.5" r="1" fill="url(#supportGradient)" />
          <circle cx="12" cy="11.5" r="1" fill="url(#supportGradient)" />
          <circle cx="15.5" cy="11.5" r="1" fill="url(#supportGradient)" />
          <defs>
            <linearGradient id="supportGradient" x1="0" y1="0" x2="24" y2="24">
              <stop offset="0%" stopColor="#6B4EE8" />
              <stop offset="100%" stopColor="#4A90E2" />
            </linearGradient>
          </defs>
        </svg>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Support chat"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 340,
            maxWidth: '90vw',
            background: 'white',
            borderRadius: 16,
            boxShadow: '0 12px 36px rgba(0,0,0,0.18)',
            border: '1px solid #eee',
            overflow: 'hidden',
            zIndex: 101,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              background: GRADIENT,
              color: 'white',
              padding: '14px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 800 }}>Support</div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>We reply by email — usually within a day</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close support"
              style={{
                background: 'rgba(255,255,255,0.18)', border: 'none', color: 'white',
                borderRadius: 8, width: 26, height: 26, cursor: 'pointer',
                fontSize: 14, lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>

          <div
            ref={scrollRef}
            style={{
              padding: 14,
              maxHeight: 320,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              background: '#faf9ff',
            }}
          >
            {thread.map((m, i) => (
              <div
                key={i}
                style={{
                  alignSelf: m.from === 'me' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  padding: '9px 13px',
                  borderRadius: m.from === 'me' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  fontSize: 13.5,
                  lineHeight: 1.45,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  ...(m.from === 'me'
                    ? { background: GRADIENT, color: 'white' }
                    : {
                      background: 'white',
                      color: m.error ? '#c0392b' : '#2b2650',
                      border: '1px solid #e8e4f8',
                    }),
                }}
              >
                {m.text}
              </div>
            ))}
            {sending && (
              <div style={{ alignSelf: 'flex-start', fontSize: 12, color: '#8a86a3', paddingLeft: 4 }}>
                Sending…
              </div>
            )}
          </div>

          <div style={{ padding: 12, borderTop: '1px solid #efecfa', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <textarea
              ref={composer.register('supportMessage')}
              {...composer.inputProps('supportMessage')}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Type your message…"
              rows={2}
              style={{
                flex: 1,
                minWidth: 0,
                resize: 'none',
                border: `2px solid ${composer.errors.supportMessage ? 'var(--color-error-border)' : '#e0daf7'}`,
                background: composer.errors.supportMessage ? 'var(--color-error-bg)' : 'white',
                borderRadius: 10,
                padding: '8px 10px',
                fontSize: 13.5,
                fontFamily: 'inherit',
                outline: 'none',
              }}
              onFocus={(e) => { if (!composer.errors.supportMessage) e.target.style.borderColor = '#6B4EE8'; }}
              onBlur={(e) => { if (!composer.errors.supportMessage) e.target.style.borderColor = '#e0daf7'; }}
            />
            <button
              type="button"
              onClick={send}
              disabled={sending}
              aria-disabled={composer.blocked ? 'true' : undefined}
              title={composer.blocked ? composer.blockedReason : undefined}
              style={{
                alignSelf: 'flex-end',
                border: 'none',
                borderRadius: 10,
                padding: '10px 16px',
                fontSize: 13.5,
                fontWeight: 700,
                cursor: sending ? 'default' : composer.blocked ? 'help' : 'pointer',
                color: 'white',
                background: GRADIENT,
                opacity: sending || composer.blocked ? 0.5 : 1,
                boxShadow: '0 2px 8px rgba(107,78,232,0.28)',
              }}
            >
              Send
            </button>
            {composer.errors.supportMessage && (
              <div id="supportMessage-error" role="alert" style={{ flexBasis: '100%', fontSize: 12.5, fontWeight: 600, color: 'var(--color-error-text)' }}>
                {composer.errors.supportMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
